import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listAllEmployees, peopleGateway } from '../index';
import { compensationPayTypeLabels } from '../../compensation/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { RetryableError } from '../../../shared/RetryableError';
import { ReadinessStatusChip, useEmployeeReadinessBatch, type ReadinessBatchLoad, type ReadinessChipValue } from '../../employee-readiness';
import { readEmployeeDetails, useEmployeeListDetails } from '../useEmployeeListDetails';
import { employeeInvitationGateway } from '../EmployeeInvitationGateway';
import { useEmployeeInvitationStates } from '../useEmployeeInvitationStates';
import { EXPORT_FORMAT_LABELS, exportEmployees, type ExportFormat, type ExportRow } from '../employeeExport';
import {
  EMPLOYEE_PROFILE_STEPS,
  employeePayMethodLabels,
  employmentStatusLabels,
  employmentTypeLabels,
  type CompanyWorkLocationOption,
  type DepartmentOption,
  type EmployeeResponse,
  type JobTitleOption,
} from '../types';
import { EmployeeStatusBadge } from './EmployeeStatusBadge';
import '../employee-list.css';

const SEARCH_DEBOUNCE_MS = 300;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const TERMINATED = 2;
const ACTIVE = 0;
/** The Role filter's "no role" choice (Job Role Library): employees whose `jobTitleId` is empty. */
const NO_ROLE = '__none';

/** A list chip's value: the server's status for that employee, or an explicit non-result state. An
 * employee the batch returned nothing for is "unavailable", never an implied pass. */
function chipFor(readiness: ReadinessBatchLoad, employeeId: string): ReadinessChipValue {
  switch (readiness.kind) {
    case 'loading': return 'loading';
    case 'forbidden': return 'forbidden';
    case 'error': return 'unavailable';
    case 'loaded': return readiness.byId[employeeId]?.status ?? 'unavailable';
  }
}

function chipText(readiness: ReadinessBatchLoad, employeeId: string): string {
  const value = chipFor(readiness, employeeId);
  switch (value) {
    case 'loading': return 'checking';
    case 'unavailable': return 'unavailable';
    case 'forbidden': return 'no access';
    default: return value;
  }
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M2.5 13.5h2.8l7.3-7.3-2.8-2.8-7.3 7.3zM11.2 2.8l2 2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

function TerminateIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5M7 7v4M9 7v4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ResendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M14 2 7 9M14 2l-4.5 12-2.5-5-5-2.5z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 10V2.5M5 5.5l3-3 3 3M3 10v3h10v-3" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** One row action: a compact icon button at rest that grows to icon + label on its own hover or keyboard focus.
 * The label is always in the accessible name, so it is announced even while visually collapsed. */
function RowAction({ label, tone, onClick, disabled, busy, title, children }: {
  label: string;
  /** Hover text when it should say more than the label (for example why the action is unavailable). */
  title?: string;
  tone: 'edit' | 'danger' | 'resend';
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`row-action row-action--${tone}`}
      aria-label={label}
      title={title ?? label}
      disabled={disabled}
      aria-busy={busy || undefined}
      onClick={onClick}
    >
      {children}
      <span className="row-action__label" aria-hidden="true">{label}</span>
    </button>
  );
}

function FolderIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <path d="M4 10a2 2 0 0 1 2-2h9l3 4h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" fill="currentColor" />
    </svg>
  );
}

interface Pill {
  key: string;
  label: string;
  aria: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

/**
 * EPN-203: the approved All Employees list. Search and the Status filter run on the server (existing list query);
 * the other filters, paging and rows-per-page run in the browser over the fetched list. BACKEND GAP: the list endpoint
 * has no employment type / pay type / pay method / branch filter and does not return pay type, pay method or branch.
 */
export function AllEmployeesList({ onAddEmployee }: { onAddEmployee: () => void }) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('');
  const [payTypeFilter, setPayTypeFilter] = useState('');
  const [payMethodFilter, setPayMethodFilter] = useState('');
  // `?branch=<work location id>` opens the list already filtered to one branch (Business Profile Setup Step 5's "Go to Employees List").
  const [searchParams] = useSearchParams();
  const [branchFilter, setBranchFilter] = useState(() => searchParams.get('branch') ?? '');
  const [roleFilter, setRoleFilter] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]!);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<EmployeeResponse | null>(null);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [jobTitles, setJobTitles] = useState<JobTitleOption[]>([]);
  const [locations, setLocations] = useState<CompanyWorkLocationOption[]>([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [exportMessage, setExportMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [resending, setResending] = useState<Set<string>>(new Set());
  const [resendMessage, setResendMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [invitationReload, setInvitationReload] = useState(0);
  const exportMenuRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Lookup names for Department / Branch. A failed or refused read just leaves those cells as a dash.
  useEffect(() => {
    let cancelled = false;
    peopleGateway.listDepartments().then((d) => { if (!cancelled) setDepartments(d); }).catch(() => undefined);
    peopleGateway.listCompanyWorkLocations().then((l) => { if (!cancelled) setLocations(l); }).catch(() => undefined);
    // Active roles, for the Role filter's options. An employee whose role is no longer active still shows it (the list item carries
    // the role title), so a failed or refused read here only shortens the filter, never hides a role name.
    peopleGateway.listJobTitles().then((j) => { if (!cancelled) setJobTitles(j); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await listAllEmployees(peopleGateway, {
        search: debouncedSearch || undefined,
        status: statusFilter === '' ? undefined : Number(statusFilter),
        sortBy: 'name',
      });
      setEmployees(all);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load people.'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load, reloadNonce]);

  const detailFilterActive = payTypeFilter !== '' || payMethodFilter !== '' || branchFilter !== '';

  // Filters that only need fields already on the list item.
  const baseFiltered = useMemo(
    () => employees.filter((e) => (nameFilter === '' || e.id === nameFilter)
      && (employmentTypeFilter === '' || String(e.employmentType ?? '') === employmentTypeFilter)
      && (roleFilter === '' || (roleFilter === NO_ROLE ? !e.jobTitleId : e.jobTitleId === roleFilter))),
    [employees, nameFilter, employmentTypeFilter, roleFilter],
  );

  const pageStartIndex = (page - 1) * rowsPerPage;
  const idsNeedingDetails = useMemo(
    () => (detailFilterActive ? baseFiltered : baseFiltered.slice(pageStartIndex, pageStartIndex + rowsPerPage)).map((e) => e.id),
    [detailFilterActive, baseFiltered, pageStartIndex, rowsPerPage],
  );
  const { details, pendingCount } = useEmployeeListDetails(employees, idsNeedingDetails);

  const filtered = useMemo(() => {
    if (!detailFilterActive) return baseFiltered;
    return baseFiltered.filter((e) => {
      const d = details[e.id];
      if (!d) return false; // not read yet: never matches; the "applying filters" note explains the wait
      return (payTypeFilter === '' || String(d.payType ?? '') === payTypeFilter)
        && (payMethodFilter === '' || String(d.payMethod ?? '') === payMethodFilter)
        && (branchFilter === '' || d.workLocationId === branchFilter);
    });
  }, [baseFiltered, detailFilterActive, details, payTypeFilter, payMethodFilter, branchFilter]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = useMemo(() => filtered.slice(firstIndex, firstIndex + rowsPerPage), [filtered, firstIndex, rowsPerPage]);

  const readiness = useEmployeeReadinessBatch(useMemo(() => pageRows.map((e) => e.id), [pageRows]));

  const departmentNames = useMemo(() => new Map(departments.map((d) => [d.id, d.name])), [departments]);
  const invitationStates = useEmployeeInvitationStates(useMemo(() => pageRows.map((e) => e.id), [pageRows]), invitationReload);
  // An accepted invitation is not "Active" until the employee submits everything: until then they are still filling in their own information.
  const isOnboarding = (e: { id: string; status: number; onboardingSubmittedAtUtc?: string | null }) =>
    invitationStates[e.id] === 'accepted' && !e.onboardingSubmittedAtUtc && e.status === ACTIVE;
  const isInvitePending = (e: { id: string; status: number }) => invitationStates[e.id] === 'pending' && e.status === ACTIVE;

  // Close the Export menu on an outside click or Escape.
  useEffect(() => {
    if (!exportOpen) return;
    const onDown = (e: MouseEvent) => { if (!exportMenuRef.current?.contains(e.target as Node)) setExportOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExportOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [exportOpen]);

  // Role names by id: the employee list item's own title first (it survives a role being archived), then the active role list.
  const roleNames = useMemo(() => {
    const names = new Map<string, string>(jobTitles.map((j) => [j.id, j.name]));
    for (const e of employees) if (e.jobTitleId && e.jobTitleName) names.set(e.jobTitleId, e.jobTitleName);
    return names;
  }, [jobTitles, employees]);

  function goToPage(next: number) {
    const clamped = Math.min(Math.max(1, next), totalPages);
    setPage(clamped);
    setPageInput(String(clamped));
  }

  // Keep the page box in step when the page is clamped by a shrinking result set.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPageInput(String(currentPage));
  }, [currentPage]);

  const filtersActive = debouncedSearch !== '' || statusFilter !== '' || nameFilter !== '' || employmentTypeFilter !== ''
    || roleFilter !== '' || detailFilterActive;

  function clearFilters() {
    setSearch(''); setDebouncedSearch(''); setStatusFilter(''); setNameFilter(''); setEmploymentTypeFilter('');
    setPayTypeFilter(''); setPayMethodFilter(''); setBranchFilter(''); setRoleFilter(''); setPage(1);
  }

  async function confirmStatusChange() {
    const emp = confirming;
    if (!emp) return;
    setConfirming(null);
    setActionError(null);
    try {
      const nextStatus = emp.status === TERMINATED ? ACTIVE : TERMINATED;
      await peopleGateway.changeEmployeeStatus(emp.id, nextStatus, nextStatus === TERMINATED ? new Date().toISOString().slice(0, 10) : null);
      await load();
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Could not change employee status.'));
    }
  }

  const nameOf = (e: EmployeeResponse) => `${e.firstName} ${e.lastName}`;

  /** Every employee matching the current search and filters, across all pages. Pay type and pay method are read for any
   * employee not already loaded, so the file is complete; a read that fails shows a dash, never a guess. */
  async function buildExportRows(): Promise<ExportRow[]> {
    const controller = new AbortController();
    const have: Record<string, typeof details[string]> = { ...details };
    const missing = filtered.filter((e) => !(e.id in have));
    const queue = [...missing];
    await Promise.all(Array.from({ length: Math.min(5, queue.length) }, async () => {
      for (let e = queue.shift(); e !== undefined; e = queue.shift()) have[e.id] = await readEmployeeDetails(e.id, controller.signal);
    }));
    return filtered.map((e) => {
      const d = have[e.id];
      return {
        'Employee Name': nameOf(e),
        Department: (e.departmentId && departmentNames.get(e.departmentId)) || '',
        'Employment Type': e.employmentType != null ? employmentTypeLabels[e.employmentType] ?? '' : '',
        'Pay Type': d?.payType != null ? compensationPayTypeLabels[d.payType] ?? '' : '',
        'Pay Method': d?.payMethod != null ? employeePayMethodLabels[d.payMethod] ?? '' : '',
        Status: isInvitePending(e) ? 'Pending Invite' : isOnboarding(e) ? 'Onboarding in Progress' : employmentStatusLabels[e.status] ?? '',
      };
    });
  }

  async function runExport(format: ExportFormat) {
    if (exporting) return;
    setExportOpen(false);
    setExportMessage(null);
    setExporting(format);
    try {
      const rows = await buildExportRows();
      await exportEmployees(format, rows);
      setExportMessage({ kind: 'ok', text: `Exported ${rows.length} ${rows.length === 1 ? 'employee' : 'employees'} as ${EXPORT_FORMAT_LABELS[format]}.` });
    } catch {
      setExportMessage({ kind: 'error', text: `Could not create the ${EXPORT_FORMAT_LABELS[format]} file. Please try again.` });
    } finally {
      setExporting(null);
    }
  }

  async function resendInvitation(emp: EmployeeResponse) {
    if (resending.has(emp.id)) return; // a second click while one is in flight is ignored
    setResending((prev) => new Set(prev).add(emp.id));
    setResendMessage(null);
    try {
      await employeeInvitationGateway.resend(emp.id, crypto.randomUUID());
      setResendMessage({ kind: 'ok', text: `Invitation resent to ${emp.firstName}.` });
      setInvitationReload((n) => n + 1);
    } catch (err) {
      setResendMessage({ kind: 'error', text: friendlyErrorMessage(err, `Could not resend the invitation to ${emp.firstName}.`) });
    } finally {
      setResending((prev) => { const next = new Set(prev); next.delete(emp.id); return next; });
    }
  }

  const pills: Pill[] = [
    {
      key: 'name', label: 'Employee Name', aria: 'Filter by employee name', value: nameFilter,
      onChange: setNameFilter,
      options: employees.map((e) => ({ value: e.id, label: nameOf(e) })),
    },
    {
      key: 'role', label: 'Role', aria: 'Filter by role', value: roleFilter,
      onChange: setRoleFilter,
      options: [
        { value: NO_ROLE, label: 'Role not assigned' },
        ...[...roleNames.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)),
      ],
    },
    {
      key: 'type', label: 'Employment Type', aria: 'Filter by employment type', value: employmentTypeFilter,
      onChange: setEmploymentTypeFilter,
      options: Object.entries(employmentTypeLabels).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'paytype', label: 'Pay Type', aria: 'Filter by pay type', value: payTypeFilter,
      onChange: setPayTypeFilter,
      options: Object.entries(compensationPayTypeLabels).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'paymethod', label: 'Pay Method', aria: 'Filter by pay method', value: payMethodFilter,
      onChange: setPayMethodFilter,
      options: Object.entries(employeePayMethodLabels).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'status', label: 'Status', aria: 'Filter by status', value: statusFilter,
      onChange: setStatusFilter,
      options: Object.entries(employmentStatusLabels).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'branch', label: 'Branch', aria: 'Filter by branch', value: branchFilter,
      onChange: setBranchFilter,
      options: locations.map((l) => ({ value: l.id, label: l.name })),
    },
  ];

  const allPageSelected = pageRows.length > 0 && pageRows.every((e) => selected.has(e.id));
  const toggleAllOnPage = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allPageSelected) pageRows.forEach((e) => next.delete(e.id)); else pageRows.forEach((e) => next.add(e.id));
    return next;
  });
  const toggleOne = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const confirmButton = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (confirming) confirmButton.current?.focus(); }, [confirming]);

  const showEmpty = !loading && !error && total === 0 && !filtersActive && employees.length === 0;
  const showNoResults = !loading && !error && total === 0 && !showEmpty;
  const showTable = !loading && !error && total > 0;

  return (
    <div className="employee-list">
      <div className="employee-list__header">
        <h2>All Employees</h2>
        <div className="employee-list__tools">
          <span className="employee-list__search-wrap">
            <SearchIcon />
            <input
              type="search" className="employee-list__search" placeholder="Search" aria-label="Search employees"
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
          </span>
          <span className="employee-list__export" ref={exportMenuRef}>
            <button
              type="button" className="employee-list__export-main" disabled={exporting !== null}
              aria-haspopup="menu" aria-expanded={exportOpen} onClick={() => setExportOpen((o) => !o)}
            >
              <ExportIcon />{exporting ? 'Exporting…' : 'Export'}
            </button>
            <button
              type="button" className="employee-list__export-caret" disabled={exporting !== null}
              aria-label="Export options" aria-haspopup="menu" aria-expanded={exportOpen} onClick={() => setExportOpen((o) => !o)}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
                <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {exportOpen && (
              <ul className="employee-list__export-menu" role="menu" aria-label="Export format">
                {(['pdf', 'xlsx', 'csv'] as ExportFormat[]).map((f) => (
                  <li key={f} role="none">
                    <button type="button" role="menuitem" onClick={() => void runExport(f)}>{EXPORT_FORMAT_LABELS[f]}</button>
                  </li>
                ))}
              </ul>
            )}
          </span>
          <button type="button" className="employee-list__add" onClick={onAddEmployee}>
            <span aria-hidden="true">+ </span>Add Employee
          </button>
        </div>
      </div>

      <div className="employee-list__filters" role="group" aria-label="Filters">
        {pills.map((p) => (
          <span className="pill" key={p.key}>
            <select
              aria-label={p.aria} value={p.value}
              onChange={(e) => { p.onChange(e.target.value); setPage(1); }}
            >
              <option value="">{p.label}</option>
              {p.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </span>
        ))}
        {filtersActive && <button type="button" className="link" onClick={clearFilters}>Clear filters</button>}
      </div>

      <div className="employee-list__bar">
        <label className="employee-list__rows">
          Rows per page:{' '}
          <select
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        {showTable && (
          <span className="muted" aria-live="polite">
            {firstIndex + 1}-{firstIndex + pageRows.length} of {total} items
          </span>
        )}
        {detailFilterActive && pendingCount > 0 && (
          <span className="muted" role="status">
            Applying filters… {idsNeedingDetails.length - pendingCount} of {idsNeedingDetails.length} employees checked.
          </span>
        )}
        {showTable && (
          <nav className="employee-list__paging" aria-label="Pagination">
            <button type="button" disabled={currentPage <= 1} onClick={() => goToPage(1)}>First</button>
            <button type="button" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>Previous</button>
            <input
              type="number" min={1} max={totalPages} aria-label="Page number" value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onBlur={() => {
                const n = Number(pageInput);
                if (Number.isFinite(n) && n >= 1) goToPage(Math.floor(n)); else setPageInput(String(currentPage));
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            />
            <span>of {totalPages}</span>
            <button type="button" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
            <button type="button" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>Last</button>
          </nav>
        )}
      </div>

      {actionError && <p className="error" role="alert">{actionError}</p>}
      {exportMessage && <p className={exportMessage.kind === 'error' ? 'error' : 'muted'} role={exportMessage.kind === 'error' ? 'alert' : 'status'}>{exportMessage.text}</p>}
      {resendMessage && <p className={resendMessage.kind === 'error' ? 'error' : 'muted'} role={resendMessage.kind === 'error' ? 'alert' : 'status'}>{resendMessage.text}</p>}
      {error && <RetryableError message={error} onRetry={() => setReloadNonce((n) => n + 1)} />}
      {loading && <p className="muted">Loading…</p>}

      {showEmpty && (
        <div className="employee-list__empty">
          <FolderIcon />
          <p>No Data Available</p>
        </div>
      )}
      {showNoResults && (
        <div className="employee-list__empty">
          <FolderIcon />
          <p>No employees found. Try changing your search or filters.</p>
        </div>
      )}

      {showTable && (
        <div className="employee-list__table-wrap">
          <table className="employee-list__table">
            <thead>
              <tr>
                <th scope="col" className="employee-list__check">
                  <input type="checkbox" aria-label="Select all employees on this page" checked={allPageSelected} onChange={toggleAllOnPage} />
                </th>
                <th scope="col">Employee Name</th>
                <th scope="col">Department</th>
                <th scope="col">Role</th>
                <th scope="col">Employment Type</th>
                <th scope="col">Pay Type</th>
                <th scope="col">Pay Method</th>
                <th scope="col">Status</th>
                <th scope="col">Payroll readiness</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((emp) => {
                const d = details[emp.id];
                const dash = (title: string) => <span className="muted" title={title}>—</span>;
                const detailCell = (value: string | undefined) => {
                  if (!d) return <span className="muted" aria-label="Loading">…</span>;
                  return value ?? dash(d.failed ? 'Unavailable' : 'Not set');
                };
                const terminated = emp.status === TERMINATED;
                return (
                  <tr key={emp.id}>
                    <td className="employee-list__check">
                      <input type="checkbox" aria-label={`Select ${nameOf(emp)}`} checked={selected.has(emp.id)} onChange={() => toggleOne(emp.id)} />
                    </td>
                    <td>{nameOf(emp)}</td>
                    <td>{(emp.departmentId && departmentNames.get(emp.departmentId)) || dash('Not set')}</td>
                    <td>
                      {emp.jobTitleId
                        ? (emp.jobTitleName ?? roleNames.get(emp.jobTitleId) ?? <span className="muted">Role unavailable</span>)
                        : <span className="muted">Role not assigned</span>}
                    </td>
                    <td>{emp.employmentType != null && employmentTypeLabels[emp.employmentType] ? employmentTypeLabels[emp.employmentType] : dash('Not set')}</td>
                    <td>{detailCell(d?.payType != null ? compensationPayTypeLabels[d.payType] : undefined)}</td>
                    <td>{detailCell(d?.payMethod != null ? employeePayMethodLabels[d.payMethod] : undefined)}</td>
                    <td>
                      {isInvitePending(emp)
                        ? <EmployeeStatusBadge kind="pending-invite" />
                        : isOnboarding(emp)
                          ? <EmployeeStatusBadge kind="onboarding" />
                          : <EmployeeStatusBadge status={emp.status} />}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="readiness-chip-button"
                        onClick={() => navigate(`/people/${emp.id}/setup-summary`)}
                        // Deliberately names no employee: the cell's accessible name would otherwise contain the employee's
                        // name and collide with the Name cell for any name-based query. The row supplies that context.
                        aria-label={`Payroll readiness: ${chipText(readiness, emp.id)}. View setup summary.`}
                      >
                        <ReadinessStatusChip value={chipFor(readiness, emp.id)} />
                      </button>
                    </td>
                    <td>
                      <span className="employee-list__actions">
                        <RowAction
                          label="Edit" tone="edit"
                          // The employee completes their own profile through the invitation; an administrator editing it meanwhile would collide with them.
                          disabled={invitationStates[emp.id] === 'pending' || isOnboarding(emp)}
                          title={
                            invitationStates[emp.id] === 'pending' ? 'Invitation pending: the employee completes their own profile'
                              : isOnboarding(emp) ? 'Onboarding in progress: the employee is completing their own profile'
                                : 'Edit'
                          }
                          onClick={() => navigate(`/people/${emp.id}/${EMPLOYEE_PROFILE_STEPS[0].key}`)}
                        >
                          <EditIcon />
                        </RowAction>
                        <RowAction label={terminated ? 'Reactivate' : 'Terminate'} tone="danger" onClick={() => setConfirming(emp)}>
                          <TerminateIcon />
                        </RowAction>
                        {invitationStates[emp.id] === 'pending' && (
                          <RowAction
                            label="Resend Invitation" tone="resend" busy={resending.has(emp.id)} disabled={resending.has(emp.id)}
                            onClick={() => void resendInvitation(emp)}
                          >
                            <ResendIcon />
                          </RowAction>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirming && (
        <div className="employee-list__scrim" onKeyDown={(e) => { if (e.key === 'Escape') setConfirming(null); }}>
          <div className="employee-list__dialog" role="dialog" aria-modal="true" aria-labelledby="employee-confirm-title">
            <h3 id="employee-confirm-title">
              {confirming.status === TERMINATED ? 'Reactivate' : 'Terminate'} {nameOf(confirming)}?
            </h3>
            <p className="muted">
              {confirming.status === TERMINATED
                ? 'This sets the status back to Active and clears the termination date.'
                : 'This sets the status to Terminated and records today as the termination date. You can reactivate the employee later.'}
            </p>
            <div className="employee-list__dialog-actions">
              <button type="button" onClick={() => setConfirming(null)}>Cancel</button>
              <button type="button" ref={confirmButton} onClick={() => void confirmStatusChange()}>
                {confirming.status === TERMINATED ? 'Reactivate' : 'Terminate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
